import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';

import enGB from 'date-fns/locale/en-GB';

import { CustomTimeInput } from '@/common//DatePickerTimeInput';
import { DatePickerHeader } from '@/common/DatePickerHeader';
import { Label, LABEL_TYPE } from '@/components/generic';
import { CalendarIcon } from '@/resources';

type DateInputProps = {
  id: string;
  selected: Date | null;
  onChange: (date: Date) => void;
  label: string;
  minDate?: Date;
  maxDate?: Date;
  errorMessage?: string;
  pickOnlyYear?: boolean;
  isInvalid?: boolean;
  isRequired: boolean;
  shouldPickTime?: boolean;
  dateFormat: string;
  touched?: () => void;
  onCalendarClose?: () => void;
  disabled?: boolean;
};

export const DateInput = ({
  id,
  selected,
  onChange,
  label,
  minDate,
  maxDate,
  errorMessage,
  pickOnlyYear,
  isInvalid,
  isRequired,
  dateFormat,
  touched,
  onCalendarClose,
  disabled = false
}: DateInputProps) => {
  const [t] = useTranslation('common');

  return (
    <div className='h-16'>
      <Label
        text={`${label}${isRequired ? ' *' : ''}`}
        type={LABEL_TYPE.PARAGRAPH2}
        className='mb-2'
      />
      <label onClick={(e) => e.preventDefault()}>
        <div
          className={`relative flex w-full items-center rounded-[10px] border ${
            isInvalid ? 'border-accent-red' : 'border-secondary-blue-gray'
          } ${disabled ? 'cursor-not-allowed bg-gray-100' : ''}`}>
          <DatePicker
            locale={enGB}
            onCalendarOpen={touched}
            onCalendarClose={onCalendarClose}
            selected={selected}
            onChange={onChange}
            dateFormat={dateFormat}
            minDate={minDate}
            maxDate={maxDate ? maxDate : pickOnlyYear === undefined ? undefined : new Date()}
            placeholderText={t('selectDate')}
            wrapperClassName='w-full cursor-pointer input:w-full'
            showYearPicker={pickOnlyYear}
            showTimeInput={dateFormat === 'dd/MM/yyyy HH:mm'}
            showPopperArrow={false}
            useWeekdaysShort
            formatWeekDay={(dayName) => dayName[0]}
            className={`w-11/12 px-3.5 focus:outline-none ${disabled ? 'cursor-not-allowed bg-gray-100' : ''}`}
            yearItemNumber={16}
            timeIntervals={5}
            shouldCloseOnSelect={dateFormat === 'dd/MM/yyyy' || dateFormat === 'yyyy'}
            customTimeInput={<CustomTimeInput date={selected ?? new Date()} id={id} />}
            disabled={disabled}
            renderCustomHeader={(props) => (
              <DatePickerHeader {...props} pickOnlyYear={!!pickOnlyYear} />
            )}
          />
          <CalendarIcon
            className='absolute right-0 mr-3 cursor-pointer'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </label>
      <div className='h-4'>
        {isInvalid && errorMessage && <p className='text-red-500'>{errorMessage}</p>}
      </div>
    </div>
  );
};
